import type { MouseEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { MemberRole, type ProtonDriveClient, type ShareNodeSettings, type ShareResult } from '@protontech/drive-sdk';
import { c } from 'ttag';

import { useAddresses } from '@proton/account/addresses/hooks';
import { Button } from '@proton/atoms/Button/Button';
import { Tooltip } from '@proton/atoms/Tooltip/Tooltip';
import {
    ContactEmailsProvider,
    FileIcon,
    type ModalStateProps,
    ModalTwo,
    Spotlight,
    useConfirmActionModal,
    useToggle,
} from '@proton/components';
import { ModalHeaderCloseButton } from '@proton/components/components/modalTwo/ModalHeader';
import { useTheme } from '@proton/components/containers/themes/ThemeProvider';
import useLoading from '@proton/hooks/useLoading';
import { IcCogWheel } from '@proton/icons/icons/IcCogWheel';
import { IcLockFilled } from '@proton/icons/icons/IcLockFilled';
import { getItem, removeItem, setItem } from '@proton/shared/lib/helpers/storage';
import { useFlag } from '@proton/unleash/useFlag';

import {
    useFlagsDriveDirectSharing,
    useFlagsDrivePublicSharing,
    useFlagsDriveSharingAdminPermissions,
} from '../../../modules/flags';
import ModalContentLoader from '../../modalUtils/ModalContentLoader';
import { DirectSharingAutocomplete } from './DirectSharing/DirectSharingAutocomplete';
import { DirectSharingFooter } from './DirectSharing/DirectSharingFooter';
import { DirectSharingInviteMessage } from './DirectSharing/DirectSharingInviteMessage';
import { DirectSharingListing } from './DirectSharing/DirectSharingListing';
import { useShareInvitees } from './DirectSharing/useShareInvitees';
import { PublicSharing } from './PublicSharing/PublicSharing';
import { useSharingSettingsModal } from './SharingSettingsModal';
import type { DirectMember, DirectSharingRole } from './interfaces';
import { EditorsManageAccessContextProvider } from './useEditorsManageAccess';

import './SharingModalView.scss';

export interface SharingModalViewProps extends ModalStateProps {
    isLoading: boolean;
    initialized: boolean;

    drive: Pick<ProtonDriveClient, 'shareNode'>;
    nodeUid: string;
    roleOnParentNode?: MemberRole;
    fileName: string;
    isPhotoOrAlbum: boolean;
    mediaType?: string;
    ownerEmail?: string;
    ownerDisplayName?: string;
    directMembers: DirectMember[];
    existingEmails: string[];
    sharingInfo: ShareResult;

    isShared: boolean;

    isPublicLinkEnabled: boolean;
    publicLink?: {
        role: MemberRole;
        url: string;
        customPassword?: string;
        expirationTime?: Date;
    };

    actions: {
        unshareNode: (email: string, options?: { refreshSharedWithMe?: boolean }) => Promise<void>;
        unsharePublic: () => Promise<void>;
        resendInvitation: (invitationUid: string) => Promise<void>;
        createSharePublic: () => Promise<void>;
        updateShareDirect: (directShareSettings: Omit<ShareNodeSettings, 'publicLink'>) => Promise<void>;
        updateSharePublic: (publicLinkSettings: {
            role?: MemberRole;
            customPassword?: string;
            expiration?: Date;
        }) => Promise<void>;
        copyInvitationLink: (invitationUid: string, email: string) => void;
        onPublicLinkToggle?: (enabled: boolean) => void;
        stopSharing: () => Promise<void>;
    };
}

const ADMIN_SPOTLIGHT_STORAGE_KEY = 'proton-drive-admin-spotlight';

export const SharingModalView = ({
    onClose,
    onExit,
    open,
    isLoading,
    drive,
    nodeUid,
    roleOnParentNode,
    fileName,
    isPhotoOrAlbum,
    mediaType,
    ownerEmail,
    ownerDisplayName,
    directMembers,
    existingEmails,
    sharingInfo,
    isPublicLinkEnabled,
    publicLink,
    initialized,
    actions,
    isShared,
}: SharingModalViewProps) => {
    const theme = useTheme();
    const isDarkTheme = theme.information.dark;

    const [addresses] = useAddresses();

    const { isDirectSharingDisabled } = useFlagsDriveDirectSharing();
    const { isPublicEditModeEnabled } = useFlagsDrivePublicSharing();
    const adminRoleEnabled = useFlagsDriveSharingAdminPermissions();
    const isSharingAdminTooltipEnabled = useFlag('DriveWebSharingAdminTooltip');

    const [settingsModal, showSettingsModal] = useSharingSettingsModal();
    const [showSpotlight, setShowSpotlight] = useState(false);

    const [confirmModal, showConfirmRemoveYourself] = useConfirmActionModal();

    const [publicLinkUpdating, withPublicLinkUpdating] = useLoading();
    const [publicLinkStateChanging, withPublicLinkStateChanging] = useLoading();
    const publicLinkBusy = publicLinkUpdating || publicLinkStateChanging;

    const [selectedRole, setRole] = useState<DirectSharingRole>(MemberRole.Editor);
    const [inviteMessage, setInviteMessage] = useState('');
    const {
        state: includeInviteMessage,
        toggle: toggleIncludeInviteMessage,
        set: setIncludeInviteMessage,
    } = useToggle(true);
    const [isAdding, withAdding] = useLoading();

    const { invitees, add: addInvitee, remove: removeInvitee, clean: cleanInvitees } = useShareInvitees(existingEmails);

    const isInvitationWorkflow = !!invitees.length;
    const isSubmitDisabled = useMemo(
        () => !invitees.length || !!invitees.find((invitee) => invitee.isLoading || invitee.error),
        [invitees]
    );
    const canChangePermissions = adminRoleEnabled && roleOnParentNode === MemberRole.Admin && !isPhotoOrAlbum;

    const isSettingsVisible =
        !isInvitationWorkflow && !publicLinkStateChanging && !publicLinkUpdating && isShared && canChangePermissions;

    const isClosedButtonDisabled = publicLinkBusy || isAdding;

    useEffect(() => {
        if (!isSharingAdminTooltipEnabled) {
            // Cleanup storage when FF will be disabled
            removeItem(ADMIN_SPOTLIGHT_STORAGE_KEY);
            return;
        }
        if (getItem(ADMIN_SPOTLIGHT_STORAGE_KEY) === 'true' || !canChangePermissions || !isShared) {
            return;
        }
        if (directMembers.some((member) => member.role === MemberRole.Admin)) {
            setItem(ADMIN_SPOTLIGHT_STORAGE_KEY, 'true');
            setShowSpotlight(true);
        }
    }, [directMembers, isSharingAdminTooltipEnabled, canChangePermissions, isShared]);

    const cleanFields = () => {
        setInviteMessage('');
        setIncludeInviteMessage(true);
        cleanInvitees();
    };

    const handleSubmitDirectSharing = (e: MouseEvent) =>
        withAdding(async () => {
            e.preventDefault();
            await actions.updateShareDirect({
                users: invitees.map((invitee) => ({
                    email: invitee.email,
                    role: selectedRole,
                })),
                emailOptions: {
                    message: inviteMessage,
                    includeNodeName: includeInviteMessage,
                },
            });
        });

    const handleChangeRole = async (email: string, role: MemberRole) => {
        // NB in this case (editors can share) the editor role doesn't exist - internally, all editors are promoted to admins
        const demotingMyself = !!addresses?.find((address) => address.Email === email) && role === MemberRole.Viewer;
        // Only users without access to the parent or without admin on parent will loose sharing privileges
        if (demotingMyself && roleOnParentNode !== MemberRole.Admin) {
            showConfirmRemoveYourself({
                title: c('Title').t`Change access to viewer?`,
                submitText: c('Action').t`Change my access`,
                message: c('Info')
                    .t`You won’t be able to share "${fileName}" after changing access to viewer. Only the owner can change your access again.`,
                onSubmit: async () => {
                    await actions.updateShareDirect({
                        users: [{ email, role }],
                    });
                    onClose();
                },
            });
        } else {
            await actions.updateShareDirect({
                users: [{ email, role }],
            });
        }
    };

    const handleUpdatePublicLink = (publicLinkSettings: {
        role?: MemberRole;
        customPassword?: string;
        expiration?: Date;
    }) => {
        return withPublicLinkUpdating(actions.updateSharePublic(publicLinkSettings));
    };

    const handleCreatePublicLink = () => {
        return withPublicLinkStateChanging(actions.createSharePublic());
    };

    const handleDeletePublicLink = () => {
        return withPublicLinkStateChanging(actions.unsharePublic());
    };

    const handleCancel = () => {
        cleanFields();
    };

    const handleRemove = async (email: string) => {
        const removingMyself = !!addresses?.find((address) => address.Email === email);
        if (removingMyself && !roleOnParentNode) {
            showConfirmRemoveYourself({
                title: c('Title').t`Remove your access?`,
                submitText: c('Action').t`Remove my access`,
                message: c('Info')
                    .t`If you remove yourself, you’ll lose access to "${fileName}" and won’t be able to open it again unless the owner shares it with you.`,
                onSubmit: async () => {
                    await actions.unshareNode(email, { refreshSharedWithMe: true });
                    onClose();
                },
            });
        } else {
            await actions.unshareNode(email);
        }
    };

    const settingsButton = (
        <Button
            icon
            shape="ghost"
            onClick={() =>
                showSettingsModal({
                    sharedFileName: fileName,
                    stopSharing: async () => {
                        await actions.stopSharing();
                        onClose();
                    },
                })
            }
            data-testid="share-modal-settings"
        >
            <IcCogWheel />
        </Button>
    );

    const renderModalContent = () => {
        // Only show loader while open — during close animation "initialized" resets to "false" but we still want the content visible
        if (!initialized) {
            return <ModalContentLoader>{c('Info').t`Loading`}</ModalContentLoader>;
        }

        return (
            <ContactEmailsProvider>
                <div className="flex items-center justify-space-between flex-nowrap border-bottom border-weak px-8 py-3 mb-5">
                    <div className="flex items-center flex-nowrap gap-2 m-0" data-testid="modal-two-header">
                        <FileIcon mimeType={mediaType ?? ''} />
                        <span className="modal-two-header-title h4 text-bold text-ellipsis">{c('Title')
                            .t`Share "${fileName}"`}</span>
                    </div>

                    <div className="grow-0 shrink-0">
                        {isSettingsVisible &&
                            (showSpotlight ? (
                                <Spotlight
                                    show={showSpotlight}
                                    type="new"
                                    onClose={() => setShowSpotlight(false)}
                                    content={
                                        <>
                                            <p className="text-bold mt-0 mb-2">{c('Spotlight')
                                                .t`Editor permissions`}</p>
                                            <p className="m-0">{c('Spotlight')
                                                .t`Editors can now manage sharing and invite others. You can turn it off anytime in Share settings.`}</p>
                                        </>
                                    }
                                    originalPlacement="top-end"
                                    isAboveModal
                                >
                                    {settingsButton}
                                </Spotlight>
                            ) : (
                                <Tooltip title={c('Tooltip').t`Change shared item settings`}>{settingsButton}</Tooltip>
                            ))}

                        <ModalHeaderCloseButton buttonProps={{ disabled: isClosedButtonDisabled }} />
                    </div>
                </div>

                <div className="px-8">
                    <DirectSharingAutocomplete
                        disabled={isDirectSharingDisabled}
                        existingEmails={existingEmails}
                        invitees={invitees}
                        onAdd={addInvitee}
                        onRemove={removeInvitee}
                        onChangeRole={setRole}
                        selectedRole={selectedRole}
                    />

                    {!isInvitationWorkflow ? (
                        <>
                            <p className="color-weak my-4">{c('Info').t`Who has access`}</p>
                            <DirectSharingListing
                                viewOnly={isDirectSharingDisabled}
                                isLoading={isLoading}
                                ownerEmail={ownerEmail}
                                ownerDisplayName={ownerDisplayName}
                                members={directMembers}
                                onRemove={handleRemove}
                                onChangeRole={handleChangeRole}
                                onResendInvitation={actions.resendInvitation}
                                onCopyInvitationLink={actions.copyInvitationLink}
                            />
                        </>
                    ) : (
                        <>
                            <DirectSharingInviteMessage
                                isAdding={isAdding}
                                inviteMessage={inviteMessage}
                                includeInviteMessage={includeInviteMessage}
                                onChangeInviteMessage={setInviteMessage}
                                onToggleIncludeInviteMessage={toggleIncludeInviteMessage}
                            />
                            <DirectSharingFooter
                                onSubmit={handleSubmitDirectSharing}
                                onCancel={handleCancel}
                                disabled={isSubmitDisabled}
                                loading={isAdding}
                                cleanFields={cleanFields}
                            />
                        </>
                    )}
                </div>
            </ContactEmailsProvider>
        );
    };

    return (
        <EditorsManageAccessContextProvider nodeUid={nodeUid} sharingInfo={sharingInfo} drive={drive}>
            <ModalTwo
                className="double-modal"
                size="large"
                open={open}
                onClose={onClose}
                onExit={onExit}
                onReset={(e: any) => {
                    e.preventDefault();
                    onClose();
                }}
                disableCloseOnEscape={publicLinkStateChanging || publicLinkUpdating || isAdding}
            >
                <div className="double-modal-content shadow-lifted mb-3">{renderModalContent()}</div>

                {!isInvitationWorkflow && isPublicLinkEnabled && !isLoading && initialized && (
                    <div className="double-modal-content shadow-lifted shrink-0">
                        <PublicSharing
                            publicLink={publicLink}
                            viewOnly={!isPublicEditModeEnabled}
                            onCreate={handleCreatePublicLink}
                            isLoading={publicLinkBusy}
                            onUpdate={handleUpdatePublicLink}
                            onDelete={handleDeletePublicLink}
                            onToggle={actions.onPublicLinkToggle}
                        />
                    </div>
                )}

                <div className="flex items-center justify-center gap-2 pt-5 shrink-0">
                    <IcLockFilled color="white" />
                    <span className={isDarkTheme ? 'color-norm' : 'color-invert'}>{c('Action')
                        .t`Sharing is end-to-end encrypted`}</span>
                </div>
            </ModalTwo>

            {settingsModal}
            {confirmModal}
        </EditorsManageAccessContextProvider>
    );
};
